import { useState, useCallback, useMemo } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";

interface OptimizedImageProps
    extends Omit<HTMLMotionProps<"img">, "src" | "onError"> {
    src: string | null | undefined;
    fallbackSrc?: string;
    errorSrc?: string;
    /** Width in pixels for optimization. Default: 400 */
    width?: number;
    /** Height in pixels for optimization. If not provided, uses width */
    height?: number;
    /** Quality from 1-100. Default: 80 */
    quality?: number;
}

/**
 * Optimized image component that uses wsrv.nl (free image CDN) to
 * serve resized and compressed images, reducing bandwidth and improving performance.
 *
 * @see https://images.weserv.nl/
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    fallbackSrc = "/images/no-image-available.jpg",
    errorSrc = "/images/broken-image.jpg",
    width = 400,
    height,
    quality = 80,
    alt,
    ...motionProps
}) => {
    const [errorCount, setErrorCount] = useState(0);

    const handleError = useCallback(() => {
        setErrorCount((prev) => prev + 1);
    }, []);

    /**
     * Gets the optimized URL using wsrv.nl image proxy
     */
    const getOptimizedUrl = useMemo(() => {
        // If no src, return fallback
        if (!src) {
            return fallbackSrc;
        }

        // If we've had 2 errors (optimized + original), show error image
        if (errorCount >= 2) {
            return errorSrc;
        }

        // If we had 1 error (optimized failed), try original
        if (errorCount === 1) {
            console.log('[OptimizedImage] Optimization failed, falling back to original:', src);
            return src;
        }

        // Skip optimization for local images
        if (src.startsWith('/') || src.startsWith('data:')) {
            return src;
        }

        // Use wsrv.nl free image CDN for optimization
        // Docs: https://images.weserv.nl/docs/
        const params = new URLSearchParams({
            url: src,
            w: width.toString(),
            h: (height ?? width).toString(),
            fit: 'cover',
            q: quality.toString(),
            output: 'webp', // Convert to WebP for better compression
        });

        const optimizedUrl = `https://images.weserv.nl/?${params.toString()}`;

        // DEBUG: Log original vs optimized URL - remove after testing
       
        return optimizedUrl;
    }, [src, fallbackSrc, errorSrc, errorCount, width, height, quality]);

    return (
        <motion.img
            loading="lazy"
            src={getOptimizedUrl}
            alt={alt}
            onError={handleError}
            {...motionProps}
        />
    );
};

export default OptimizedImage;
